/**
 * useRoles — lightweight shared hook for the admin-managed role roster.
 *
 * Roles are the single source of truth for shift roles across the whole
 * app: Upcoming Shifts, Shift Calendar, Staff Requirements block, employee
 * assignment dropdowns and (future) payroll views. Every consumer imports
 * from here so any change made in `/admin/roles` propagates automatically.
 *
 * The hook exposes:
 *   - roles:            full list (active + archived), sorted server-side
 *   - activeRoles:      convenience filter for dropdowns that must only
 *                       surface currently-usable roles
 *   - rolesBySlug:      slug → role object
 *   - loading / error:  request state
 *   - refresh():        force re-fetch (e.g. after an admin creates a role)
 *
 * NOTE: the API endpoint (`GET /api/roles`) is authenticated but readable by
 * every role (admin/supervisor/employee) so employee-facing forms can also
 * consume it without a permissions detour.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Historical seed roles that legacy events / shifts might still reference
// but that could have been deleted from the roles collection. The lookup
// falls back to this map so payroll history always renders a friendly
// label ("Cleaner" instead of "cleaner").
const SEED_FALLBACK = {
  sia_door_supervisor: 'SIA Door Supervisor',
  cso_steward: 'CSO Steward',
  cleaner: 'Cleaner',
  supervisor: 'Supervisor',
};

export function useRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/roles`);
      setRoles(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(e);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const activeRoles = useMemo(() => roles.filter((r) => r.active), [roles]);
  const rolesBySlug = useMemo(() => {
    const map = {};
    for (const r of roles) map[r.slug] = r;
    return map;
  }, [roles]);

  const roleName = useCallback((slug) => {
    if (!slug) return '';
    if (rolesBySlug[slug]) return rolesBySlug[slug].name;
    return SEED_FALLBACK[slug] || slug;
  }, [rolesBySlug]);

  return { roles, activeRoles, rolesBySlug, roleName, loading, error, refresh };
}

export const CURRENCY_OPTIONS = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];
export const currencySymbol = (code) =>
  CURRENCY_OPTIONS.find((c) => c.code === code)?.symbol || '£';
